/* Movatic WMS — Frontend | CesarFilho 932048016 */
// ADR-0034 F4b-3 (-i): CatalogViewer — execução guiada de tarefa (mecânica; SEM código
// escaneável, que é a -ii). Consome next-step (o passo/expected_code) + o catálogo dev
// (build_catalog). Superuser/dev (o /catalog/ exige superuser + warehouse sandbox).
//
// CONTRATO REAL (verificado no recon; difere do que specs antigos assumiam):
//  - next-step: current_step ∈ machine|support|location|pick|done|not_started; done =
//    current_step==="done"; expected_code só p/ location|pick; current_line traz o
//    reservation_id/qty do pick.
//  - catalog visual.type ∈ "image"(product/equipment, tem url) | "geometry"(location) |
//    "generic_by_kind"(support, hu_kind). NÃO há "image_url"/"icon_by_category".
//  - actions (campos REAIS): bind_support{handling_unit_code} · start{scan} ·
//    confirm_pick{reservation_id, scan}. (qty é opcional no confirm_pick -> omitido.)
import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import JsBarcode from 'jsbarcode'
import QRCode from 'react-qr-code'
import { useAuthStore } from '@/store/authStore'
import { TaskOfferContext } from '@/mobile/components/TaskOfferProvider'

// -------- auth (fetch()+buildAuthHeaders, molde AbsencesPage; NÃO axios) --------
function buildAuthHeaders(contentTypeJson = true): HeadersInit {
  const state =
    (
      useAuthStore as unknown as { getState?: () => { accessToken?: string } }
    ).getState?.() ?? {}
  const token = state.accessToken
  const headers: Record<string, string> = {}
  if (contentTypeJson) headers['Content-Type'] = 'application/json'
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

// -------- tipos do contrato --------
type StepKind =
  | 'machine'
  | 'support'
  | 'location'
  | 'pick'
  | 'deliver'
  | 'done'
  | 'not_started'
export type NextStep = {
  task_id: number
  status: string
  current_step: StepKind
  step_applicable: boolean
  expected_code: string | null
  current_line: null | {
    sequence: number
    product_code: string
    qty: string
    location_code: string | null
    reservation_id: number | null
  }
  remaining_lines: number
}
type Visual =
  | { type: 'image'; url: string | null }
  | { type: 'geometry'; [k: string]: unknown }
  | { type: 'generic_by_kind'; hu_kind: string }
export type CatEntity = {
  kind: string
  code: string
  name: string
  visual: Visual
}
export type Catalog = {
  warehouse?: unknown
  locations: CatEntity[]
  equipment: CatEntity[]
  products: CatEntity[]
  supports: CatEntity[]
}

const DEFAULT_WAREHOUSE = '540067' // sandbox (dev)

// -------- desenhos genéricos (SVG inline; sem ficheiros) --------
const svgBox = 'w-full h-28 text-neutral-500 dark:text-neutral-400'

function PalletSVG() {
  return (
    <svg
      viewBox="0 0 64 64"
      className={svgBox}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="8" y="20" width="48" height="10" />
      <rect x="8" y="34" width="48" height="10" />
      <path d="M14 44v8M32 44v8M50 44v8" />
    </svg>
  )
}
function ForkliftSVG() {
  return (
    <svg
      viewBox="0 0 64 64"
      className={svgBox}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M10 44h20v-14h10l8 10v4" />
      <path d="M40 14v30M40 22h12" />
      <circle cx="18" cy="50" r="4" />
      <circle cx="34" cy="50" r="4" />
    </svg>
  )
}
function ShelfSVG() {
  return (
    <svg
      viewBox="0 0 64 64"
      className={svgBox}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="10" y="12" width="44" height="40" />
      <path d="M10 26h44M10 40h44M32 12v40" />
    </svg>
  )
}
function CubeSVG() {
  return (
    <svg
      viewBox="0 0 64 64"
      className={svgBox}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M32 10l20 10v22L32 54 12 42V20z" />
      <path d="M12 20l20 10 20-10M32 30v24" />
    </svg>
  )
}

export function CardVisual({ entity }: { entity: CatEntity }) {
  const v = entity.visual as Visual
  if (v?.type === 'image' && v.url) {
    return (
      <img
        src={v.url}
        alt={entity.name}
        className="w-full h-28 object-contain"
      />
    )
  }
  if (v?.type === 'geometry' || entity.kind === 'location') return <ShelfSVG />
  if (entity.kind === 'equipment') return <ForkliftSVG />
  if (v?.type === 'generic_by_kind')
    return v.hu_kind === 'PALLET' ? <PalletSVG /> : <CubeSVG />
  return <CubeSVG />
}

// ADR-0034 F4b-3-ii: código ESCANEÁVEL do card.code. Regra D7: produto -> Code128 (jsbarcode);
// resto (support/equipment/location) -> QR. SEMPRE preto-sobre-branco numa caixa branca FIXA
// (um scanner físico lê contraste real -> NÃO seguir o dark mode).
export function Barcode({ value }: { value: string }) {
  const ref = useRef<SVGSVGElement>(null)
  useEffect(() => {
    if (!ref.current) return
    try {
      JsBarcode(ref.current, value, {
        format: 'CODE128',
        height: 40,
        width: 1.4,
        margin: 0,
        displayValue: false,
        background: '#ffffff',
        lineColor: '#000000',
      })
    } catch {
      // code não codificável em Code128 -> svg vazio (fallback silencioso).
    }
  }, [value])
  return <svg ref={ref} className="max-w-full h-auto" />
}

export function ScanCode({ entity }: { entity: CatEntity }) {
  return (
    <div className="mt-2 flex justify-center">
      {/* caixa branca fixa: preto-sobre-branco em qualquer tema (leitura real por scanner). */}
      <div className="bg-white p-1 rounded max-w-full overflow-hidden">
        {entity.kind === 'product' ? (
          <Barcode value={entity.code} />
        ) : (
          <QRCode
            value={entity.code}
            size={90}
            fgColor="#000000"
            bgColor="#ffffff"
          />
        )}
      </div>
    </div>
  )
}

// -------- helpers de passo --------
const STEP_LABEL: Record<string, string> = {
  support: 'vincular suporte',
  location: 'ir ao endereço',
  pick: 'picar produto',
}

function instruction(ns: NextStep): string {
  if (ns.current_step === 'support')
    return 'Pica um suporte (qualquer suporte válido serve)'
  if (ns.current_step === 'location')
    return `Pica o endereço ${ns.expected_code ?? ''}`
  if (ns.current_step === 'pick')
    return `Pica o produto ${ns.expected_code ?? ''}`
  return ''
}

// Fisher-Yates (Math.random é browser-JS, ok aqui).
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// Monta 3 cards: o certo (kind = passo) + 2 distratores de OUTROS kinds (um de cada), baralhados.
export function buildCards(ns: NextStep, cat: Catalog): CatEntity[] {
  const currentKind = ns.current_step === 'pick' ? 'product' : ns.current_step // support|location|product
  let correct: CatEntity | undefined
  if (ns.current_step === 'support') correct = cat.supports?.[0]
  else if (ns.current_step === 'location')
    correct = cat.locations?.find((l) => l.code === ns.expected_code)
  else if (ns.current_step === 'pick')
    correct = cat.products?.find((p) => p.code === ns.expected_code)

  const pools: { kind: string; items: CatEntity[] }[] = [
    { kind: 'support', items: cat.supports ?? [] },
    { kind: 'location', items: cat.locations ?? [] },
    { kind: 'product', items: cat.products ?? [] },
    { kind: 'equipment', items: cat.equipment ?? [] },
  ]
  const distractors = shuffle(
    pools.filter((p) => p.kind !== currentKind && p.items.length > 0),
  )
    .slice(0, 2)
    .map((p) => p.items[Math.floor(Math.random() * p.items.length)])

  return shuffle([correct, ...distractors].filter(Boolean) as CatEntity[])
}

export default function CatalogViewer() {
  // Modo-operador (PUSH): /m/catalogo?task=NNN, aberto pelo Aceitar do TaskOfferPopup. Sem
  // param -> modo-sandbox (superuser, inputs manuais) intacto. offerCtx e' null fora do /m
  // (rota desktop /catalogo, sem provider) -> nao rebenta.
  const [searchParams] = useSearchParams()
  const paramTask = searchParams.get('task')
  const operatorMode = !!paramTask
  const navigate = useNavigate()
  const offerCtx = useContext(TaskOfferContext)

  const [warehouse, setWarehouse] = useState(DEFAULT_WAREHOUSE)
  const [taskId, setTaskId] = useState(paramTask ?? '')
  const [nextStep, setNextStep] = useState<NextStep | null>(null)
  const [cards, setCards] = useState<CatEntity[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)

  async function fetchNextStep(): Promise<NextStep> {
    const r = await fetch(`/api/workforce/tasks/${taskId}/next-step/`, {
      headers: buildAuthHeaders(),
    })
    if (!r.ok) throw new Error('next-step')
    return (await r.json()) as NextStep
  }

  // ADR-0034 F4b-3 perf: catálogo LIMITADO (limit=20 por tipo, corta ~54 mil -> ~60) +
  // include do card certo. location/pick têm expected_code -> include garante-o mesmo além do
  // limit; support é null -> só limit (qualquer suporte serve). Re-buscado por-passo (o
  // include muda entre location e pick).
  async function fetchCatalog(ns: NextStep): Promise<Catalog | null> {
    // limit + include (garante o expected_code alem do limit) sao iguais nos dois modos.
    const params = new URLSearchParams({ limit: '20' })
    if (ns.current_step === 'location' && ns.expected_code)
      params.set('include', `location:${ns.expected_code}`)
    else if (ns.current_step === 'pick' && ns.expected_code)
      params.set('include', `product:${ns.expected_code}`)
    // ADR-0034 F4b-4: dois destinos por modo. Operador (?task) -> catalogo de EXECUCAO do
    // coletor, gated a operador, warehouse DERIVADO da task no backend (sem ?warehouse=).
    // Sandbox (superuser) -> endpoint dev com ?warehouse= (INTACTO, nao se toca).
    let catalogUrl: string
    if (operatorMode) {
      catalogUrl = `/api/workforce/tasks/${taskId}/catalog/?${params.toString()}`
    } else {
      params.set('warehouse', warehouse)
      catalogUrl = `/api/movatic-dev/catalog/?${params.toString()}`
    }
    const r = await fetch(catalogUrl, { headers: buildAuthHeaders() })
    return r.ok ? ((await r.json()) as Catalog) : null
  }

  function refreshCards(ns: NextStep, cat: Catalog | null) {
    if (cat && ['support', 'location', 'pick'].includes(ns.current_step))
      setCards(buildCards(ns, cat))
    else setCards([])
  }

  async function load() {
    if (!taskId.trim()) return
    setLoading(true)
    setFeedback(null)
    try {
      // next-step PRIMEIRO (dá o passo + expected_code) -> catálogo limitado desse passo.
      const ns = await fetchNextStep()
      const cat = await fetchCatalog(ns)
      setNextStep(ns)
      refreshCards(ns, cat)
    } catch {
      setFeedback(
        'Falha ao carregar (confirma superuser + warehouse sandbox + taskId).',
      )
    } finally {
      setLoading(false)
    }
  }

  async function pickCard(card: CatEntity) {
    if (!nextStep || busy) return
    const step = nextStep.current_step
    let path = ''
    let body: Record<string, unknown> = {}
    if (step === 'support') {
      path = 'bind_support'
      body = { handling_unit_code: card.code }
    } else if (step === 'location') {
      path = 'start'
      body = { scan: card.code }
    } else if (step === 'pick') {
      path = 'confirm_pick'
      body = {
        reservation_id: nextStep.current_line?.reservation_id,
        scan: card.code,
      }
    } else {
      return
    }

    setBusy(true)
    setFeedback(null)
    try {
      const res = await fetch(`/api/workforce/tasks/${taskId}/${path}/`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify(body),
      })
      if (res.ok) {
        const ns = await fetchNextStep() // avança
        const cat = await fetchCatalog(ns) // re-busca (o include muda com o passo)
        setNextStep(ns)
        refreshCards(ns, cat)
      } else {
        // Estado NEUTRO (sem vermelho, decisão owner): não avançou, mantém o passo.
        const d = (await res.json().catch(() => ({}))) as { detail?: string }
        setFeedback(d?.detail || 'Não avançou.')
      }
    } catch {
      setFeedback('Não avançou.')
    } finally {
      setBusy(false)
    }
  }

  // Modo-operador: carrega a task UMA vez ao montar (sem o botao). O taskId ja' vem do param;
  // o ref evita re-disparo. O fluxo remonta o viewer por task (passa sempre por /m antes do
  // proximo PUSH), por isso "1x ao montar" cobre cada tarefa.
  const autoloadedRef = useRef(false)
  useEffect(() => {
    if (operatorMode && !autoloadedRef.current) {
      autoloadedRef.current = true
      load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- so' ao montar (1x por task)
  }, [])

  // Costura 4: em modo-operador, ao chegar a 'done', limpa a task ativa do provider
  // (clearActive -> o poll do PUSH volta a aterrar) e volta a /m (idle). Modo-sandbox (offerCtx
  // null / sem param) NAO faz isto -> mantem o comportamento atual do 'done'.
  useEffect(() => {
    if (!operatorMode || nextStep?.current_step !== 'done') return
    setFeedback('Tarefa concluída ✓')
    const t = setTimeout(() => {
      offerCtx?.clearActive()
      navigate('/m')
    }, 1200)
    return () => clearTimeout(t)
  }, [operatorMode, nextStep, offerCtx, navigate])

  const step = nextStep?.current_step
  const isScanStep =
    step === 'support' || step === 'location' || step === 'pick'

  return (
    <div className="card p-4">
      <h2 className="text-lg font-semibold">Execução guiada (catálogo)</h2>

      {/* Seletor manual (warehouse + taskId): SO' no modo-sandbox. No modo-operador (PUSH via
          ?task) esconde-se -> o operador nao escolhe nem edita nada; warehouse = DEFAULT. */}
      {!operatorMode && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label">Warehouse (sandbox)</label>
            <input
              className="input"
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Task ID</label>
            <input
              className="input"
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
              placeholder="ex.: 123"
            />
          </div>
          <div className="flex items-end">
            <button
              className="btn"
              onClick={load}
              disabled={loading || !taskId.trim()}
            >
              {loading ? 'A carregar…' : 'Carregar'}
            </button>
          </div>
        </div>
      )}

      {/* Estado do passo */}
      {nextStep && (
        <div className="mt-5">
          {step === 'done' ? (
            <div className="text-lg font-medium text-green-600">
              ✓ Tarefa concluída
            </div>
          ) : isScanStep ? (
            <>
              <div className="flex items-baseline gap-3">
                <span className="text-base font-semibold">
                  Passo: {STEP_LABEL[step as string]}
                </span>
                <span className="text-sm text-neutral-500">
                  faltam {nextStep.remaining_lines} linhas
                </span>
              </div>
              <p className="mt-1 text-neutral-600 dark:text-neutral-300">
                {instruction(nextStep)}
              </p>

              {/* 3 cards */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {cards.map((c, i) => (
                  <button
                    key={`${c.kind}-${c.code}-${i}`}
                    className="card p-3 text-center hover:ring-2 hover:ring-blue-400 disabled:opacity-50"
                    onClick={() => pickCard(c)}
                    disabled={busy}
                  >
                    <CardVisual entity={c} />
                    <div className="mt-2 font-medium truncate">
                      {c.name || c.code}
                    </div>
                    <div className="text-xs text-neutral-500">{c.code}</div>
                    <ScanCode entity={c} />
                  </button>
                ))}
              </div>
            </>
          ) : (
            // machine / not_started: não é passo de scan nesta tela (decisão owner).
            <div className="text-neutral-600 dark:text-neutral-300">
              Passo «{step}» não se pica aqui — aceita/vincula a máquina no
              coletor. Recarrega quando a tarefa estiver em execução.
            </div>
          )}

          {feedback && (
            <div className="mt-3 text-sm text-neutral-500">{feedback}</div>
          )}
        </div>
      )}
    </div>
  )
}
